import { Tag } from '@nimbus-ds/components';
import { MenuButton } from '@nimbus-ds/patterns';
import { useTranslation } from 'react-i18next';
import { useLocation, Link } from 'react-router-dom';
import { StatsIcon } from '@tiendanube/icons';
import {
  LIVEVIEW_ACCESS,
  NUBEINSIGHTS_VISITS,
  NUBEINSIGHTS_PRODUCTS,
  NUBEINSIGHTS_SALES,
  NUBEINSIGHTS_GENERAL,
  NUBEINSIGHTS_SEARCH_INSIGHTS,
} from 'App/featuresFlags';
import { useHandleMenuClick, useHasTags } from 'App/hooks';
import { MenuItem } from 'App/settings/menu/components';
import { useMenu } from 'App/settings/menu/hooks';
import { trackingMenuPrimaryStatsClick } from 'App/tracking';
import { useResponsive } from 'commons/components';
import { STATS_ROUTES } from 'domains/Statistics/statisticsRoutes';
import {
  menuSecondaryStatsClick,
  menuSecondaryStatsProductsClick,
  menuSecondaryStatsPaymentsClick,
  menuSecondaryStatsAcquisitionClick,
  menuSecondaryStatsShippingClick,
  menuSecondaryStatsGeneralClick,
  menuSecondaryStatsLiveviewClick,
  menuSecondaryStatsCouponsClick,
  menuSecondaryStatsSearchInsightsClick,
  menuSecondaryStatsVisitsClick,
  menuSecondaryStatsSalesClick,
  menuSecondaryStatsProductsV2Click,
} from './tracking';

function StatsMenu() {
  const { t } = useTranslation('common');
  const { menu } = useMenu();

  const { pathname, hash } = useLocation();
  const { handleClickItem } = useHandleMenuClick();
  const [
    hasLiveviewAccess,
    hasVisits,
    hasProducts,
    hasSales,
    hasGeneral,
    hasSearchInsights,
  ] = useHasTags([
    LIVEVIEW_ACCESS,
    NUBEINSIGHTS_VISITS,
    NUBEINSIGHTS_PRODUCTS,
    NUBEINSIGHTS_SALES,
    NUBEINSIGHTS_GENERAL,
    NUBEINSIGHTS_SEARCH_INSIGHTS,
  ]);
  const { isMobile } = useResponsive();

  /**
   * Menu Options to Prax Analytics
   * Prax is a third party app
   *
   * TODO
   * - Move Stats menu to BFF
   */
  const moreOptionMenu = menu
    ? Object.values(menu?.Main.Top.options?.Statistics.options ?? {})
    : [];

  const withMenuOption = moreOptionMenu.some((item) => {
    hash;
    const [pathnameItem] = item.goTo?.split('#') ?? [];
    return pathnameItem === pathname;
  });

  const isActiveMenu =
    pathname.startsWith(STATS_ROUTES.stats) || withMenuOption;

  const SUB_ITEMS = [
    {
      children: t('menu.stats.generals'),
      onClick: () => {
        menuSecondaryStatsClick();
        handleClickItem();
      },
      active: pathname === STATS_ROUTES.stats,
      href: STATS_ROUTES.stats,
    },
    {
      children: t('menu.stats.payments'),
      onClick: () => {
        menuSecondaryStatsPaymentsClick();
        handleClickItem();
      },
      active: pathname === STATS_ROUTES.payments,
      href: STATS_ROUTES.payments,
    },
    {
      children: t('menu.stats.shipping'),
      onClick: () => {
        menuSecondaryStatsShippingClick();
        handleClickItem();
      },
      active: pathname === STATS_ROUTES.shipping,
      href: STATS_ROUTES.shipping,
    },
    {
      children: t('menu.stats.products'),
      onClick: () => {
        menuSecondaryStatsProductsClick();
        handleClickItem();
      },
      active: pathname === STATS_ROUTES.products,
      href: STATS_ROUTES.products,
    },
    {
      children: t('menu.stats.acquisition'),
      onClick: () => {
        menuSecondaryStatsAcquisitionClick();
        handleClickItem();
      },
      active: pathname === STATS_ROUTES.acquisition,
      href: STATS_ROUTES.acquisition,
    },
  ];

  const handleMenuStatsClick = () => {
    trackingMenuPrimaryStatsClick();
    handleClickItem();
  };

  const nubeinsightsRoutes = [
    STATS_ROUTES.nubeinsightsLiveview,
    STATS_ROUTES.nubeinsightsSearchInsights,
    STATS_ROUTES.nubeinsightsCoupons,
    STATS_ROUTES.nubeinsightsGeneral,
    STATS_ROUTES.nubeinsightsVisits,
    STATS_ROUTES.nubeinsightsProducts,
    STATS_ROUTES.nubeinsightsSales,
  ];

  if (!isMobile) {
    if (hasLiveviewAccess) {
      SUB_ITEMS.push({
        children: t('menu.stats.liveview'),
        onClick: () => {
          menuSecondaryStatsLiveviewClick();
          handleClickItem();
        },
        active: pathname === STATS_ROUTES.nubeinsightsLiveview,
        href: STATS_ROUTES.nubeinsightsLiveview,
      });
      SUB_ITEMS.push({
        children: t('menu.stats.coupons'),
        onClick: () => {
          menuSecondaryStatsCouponsClick();
          handleClickItem();
        },
        active: pathname === STATS_ROUTES.nubeinsightsCoupons,
        href: STATS_ROUTES.nubeinsightsCoupons,
      });
    }
    if (hasGeneral) {
      SUB_ITEMS.push({
        children: t('menu.stats.general'),
        onClick: () => {
          menuSecondaryStatsGeneralClick();
          handleClickItem();
        },
        active: pathname === STATS_ROUTES.nubeinsightsGeneral,
        href: STATS_ROUTES.nubeinsightsGeneral,
      });
    }
    if (hasVisits) {
      SUB_ITEMS.push({
        children: t('menu.stats.visits'),
        onClick: () => {
          menuSecondaryStatsVisitsClick();
          handleClickItem();
        },
        active: pathname === STATS_ROUTES.nubeinsightsVisits,
        href: STATS_ROUTES.nubeinsightsVisits,
      });
    }
    if (hasProducts) {
      SUB_ITEMS.push({
        children: t('menu.stats.products'),
        onClick: () => {
          menuSecondaryStatsProductsV2Click();
          handleClickItem();
        },
        active: pathname === STATS_ROUTES.nubeinsightsProducts,
        href: STATS_ROUTES.nubeinsightsProducts,
      });
    }
    if (hasSearchInsights) {
      SUB_ITEMS.push({
        children: t('menu.stats.searchInsights'),
        onClick: () => {
          menuSecondaryStatsSearchInsightsClick();
          handleClickItem();
        },
        active: pathname === STATS_ROUTES.nubeinsightsSearchInsights,
        href: STATS_ROUTES.nubeinsightsSearchInsights,
      });
    }
    if (hasSales) {
      SUB_ITEMS.push({
        children: t('menu.stats.sales'),
        onClick: () => {
          menuSecondaryStatsSalesClick();
          handleClickItem();
        },
        active: pathname === STATS_ROUTES.nubeinsightsSales,
        href: STATS_ROUTES.nubeinsightsSales,
      });
    }
  }

  return (
    <MenuButton.Accordion
      contentid="content-stats"
      menuButton={{
        id: 'control-stats',
        label: t('menu.stats.title'),
        startIcon: StatsIcon,
        'aria-controls': 'content-stats',
        onClick: handleMenuStatsClick,
      }}
      open={isActiveMenu}
      active={isActiveMenu}
      as={Link}
      to={STATS_ROUTES.stats}
    >
      {SUB_ITEMS.map((item, index) => (
        <MenuButton
          key={index}
          as={Link}
          label={item.children}
          onClick={item.onClick}
          to={item.href}
          active={item.active}
        >
          {nubeinsightsRoutes.includes(item.href) && (
            <Tag appearance="primary">Beta</Tag>
          )}
        </MenuButton>
      ))}
      {moreOptionMenu.map((option) => (
        <MenuItem option={option} key={option.key} />
      ))}
    </MenuButton.Accordion>
  );
}

export default StatsMenu;
